import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Building2, Users, Plug, FileText, History, Save, FileSignature, BellRing, PoundSterling } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { SettingsProvider, useSettings } from './useSettings';

const TABS = [
    { to: '/settings/company',      label: 'Company',      icon: Building2, action: 'edit'   },
    { to: '/settings/users',        label: 'Users & Permissions', icon: Users,    action: 'view'   },
    { to: '/settings/integrations', label: 'Integrations', icon: Plug,     action: 'edit'   },
    { to: '/settings/terms',        label: 'Templates & T&Cs', icon: FileText, action: 'edit' },
    { to: '/settings/reminders',    label: 'Payment reminders', icon: BellRing, action: 'edit' },
    { to: '/settings/profitability', label: 'Profitability', icon: PoundSterling, action: 'edit' },
    { to: '/settings/nda-template', label: 'NDA template', icon: FileSignature, action: 'edit' },
    { to: '/settings/audit-log',    label: 'Audit log',    icon: History,  action: 'view'   },
];

export default function SettingsLayout() {
    return (
        <SettingsProvider>
            <SettingsShell />
        </SettingsProvider>
    );
}

function SettingsShell() {
    const { user, can } = useAuth();
    const { settings, saving, save } = useSettings();
    const { pathname } = useLocation();

    const showSaveAll = ['/settings/company', '/settings/terms', '/settings/profitability'].some(p => pathname.startsWith(p));
    const canEditSettings = can('settings', 'edit');

    return (
        <div data-testid="settings-page" className="bm-fade-in">
            <PageHeader
                title="Settings"
                description="Company defaults, integrations, user permissions and templates."
                actions={
                    showSaveAll && canEditSettings && (
                        <Button onClick={() => save()} disabled={saving || !settings} className="h-9"
                                style={{ background: 'var(--bm-primary)' }} data-testid="settings-save-btn">
                            <Save size={15} className="mr-1.5" /> {saving ? 'Saving…' : 'Save all'}
                        </Button>
                    )
                }
            />

            <div className="px-8 pt-2">
                <div className="flex flex-wrap gap-1 border-b border-[color:var(--bm-border)] -mx-1" data-testid="settings-tabs">
                    {TABS.filter(t => can('settings', t.action)).map(tab => {
                        const Icon = tab.icon;
                        return (
                            <NavLink
                                key={tab.to}
                                to={tab.to}
                                className={({ isActive }) =>
                                    `inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition ${
                                        isActive
                                            ? 'border-[color:var(--bm-accent)] text-[color:var(--bm-text)]'
                                            : 'border-transparent text-[color:var(--bm-text-muted)] hover:text-[color:var(--bm-text)]'
                                    }`
                                }
                                data-testid={`settings-tab-${tab.to.split('/').pop()}`}
                            >
                                <Icon size={14} />
                                {tab.label}
                            </NavLink>
                        );
                    })}
                </div>
            </div>

            <div className="px-8 py-6">
                {!settings && pathname !== '/settings/users' && pathname !== '/settings/audit-log' && pathname !== '/settings/nda-template' ? (
                    <div className="text-[color:var(--bm-text-muted)]">Loading…</div>
                ) : (
                    <Outlet />
                )}
            </div>
        </div>
    );
}

export function SettingsSection({ title, description, children, wide, testId }) {
    return (
        <div className="bg-white rounded-xl border border-[color:var(--bm-border)] p-6 mb-5 max-w-4xl" data-testid={testId}>
            <div className="mb-4">
                <div className="font-display text-[15px] font-semibold text-[color:var(--bm-text)]">{title}</div>
                {description && <div className="text-[12px] text-[color:var(--bm-text-muted)] mt-1">{description}</div>}
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${wide ? 'sm:gap-2' : ''}`}>
                {children}
            </div>
        </div>
    );
}
